import { logger } from '@parrot-software/observability';

import type { ICsfApiPort } from '@subscriptions/application/ports';
import type { CsfDataViewDto } from '@subscriptions/application/dtos';
import { extractErrorAttributes } from '@subscriptions/application/utils';

const invoiceLog = logger.forDomain('SUB', { subdomain: 'billing' });

/**
 * Use case for reading the fiscal data out of a CSF pdf.
 *
 * Nothing is persisted: the result only prefills the form the user then confirms. The document
 * carries the taxpayer's RFC and name, so the outcome is logged as field presence only (§5.1).
 *
 * @param port - CSF API port
 * @returns Use case executor
 */
export const extractCsfDataUseCase = (port: ICsfApiPort) => {
  const execute = async (file: File): Promise<CsfDataViewDto> => {
    try {
      const csfData = await port.extract(file);

      invoiceLog.info('extractCsfData', {
        extractHasRfc: Boolean(csfData.rfc),
        extractHasTaxRegime: Boolean(csfData.taxRegimeDescription),
        extractHasPostalCode: Boolean(csfData.postalCode),
      });

      return csfData;
    } catch (error) {
      invoiceLog.error('extractCsfData', extractErrorAttributes(error));
      throw error;
    }
  };

  return { execute };
};
